import * as React from 'react';
import {observer} from 'mobx-react';
import {classes, stylesheet} from 'typestyle';

import {formatValue, NumericFormatter} from '../../../utility/FormatValueUtils';
import {DateRange} from '../../../stores/TimeIntervalStore';
import {Channel} from '../../../types/Channel';
import {KPIGroupKey} from '../../../kpi';
import {NoneKPIColumns} from '../../../stores/GeneralSelectorTableStore';
import {spinner} from '../../../styles/style';
import {TableDataForChannelResponse} from '../../../types/api/tableData/TableDataForChannel';
import {MergedBuildingBlockInstructions, ToggleableSelectorTableColumnDef} from '../../types/Report';
import {DIAGRAM_COLORS, getReadableTextColor} from '../../../utility/ColorUtils';
import TablePreviewStore, {TablePreviewState} from '../../stores/TablePreviewStore';
import {CiBlueColor, BlueColor, TableRowBackgroundColor, BlackColor, WhiteColor, GreyColor} from '../../../styles/colors';

export interface TablePreviewInBlockProps {
    targetCurrency?: string;
    diagramCompanionMode?: boolean;
    lineLimit?: number;
    columns: ToggleableSelectorTableColumnDef[];
    moneyFormatToTargetCurrency: (fractions?: number) => NumericFormatter;
    mergedBuildingBlockInstructions: MergedBuildingBlockInstructions;
    dateRange: DateRange;
    compareDateRange?: DateRange;
    getFullChannelForChannelName: (channelName: string) => Channel;
    previewModeEnabled: boolean;
}

@observer
export default class TablePreview extends React.Component<TablePreviewInBlockProps> {
    private tablePreviewStore: TablePreviewState = new TablePreviewStore();

    public componentDidMount() {
        this.tablePreviewStore.checkForTableUpdate(this.props);
    }

    public componentWillReceiveProps(newProps: TablePreviewInBlockProps) {
        this.tablePreviewStore.checkForTableUpdate(newProps, this.props);
    }

    private renderColumnHeaders(): React.ReactNode {
        return this.props.columns
            .filter((column) => column.isVisible)
            .map((column) => (
                <th className={tableCss.tableHeaderCell} key={column.dataKey}>
                    {column.label}
                </th>
            ));
    }

    private renderLoadingSpinner() {
        return (
            <div className={tableCss.loadingSpinnerCentering}>
                <div className={tableCss.loadingSpinnerContainer}>
                    <div className={spinner(80, 'transparent', BlueColor)} />
                </div>
            </div>
        );
    }

    private getColorForIndex(colorIndex: number): string {
        if (colorIndex < 0) {
            return 'transparent';
        }
        return DIAGRAM_COLORS[colorIndex] ? DIAGRAM_COLORS[colorIndex].toString() : BlackColor;
    }

    private getBackgroundColorForCell(rowData: TableDataForChannelResponse, columnDef: ToggleableSelectorTableColumnDef): string {
        if (!this.props.diagramCompanionMode) {
            return 'transparent';
        }

        const {diagramInstruction} = this.props.mergedBuildingBlockInstructions;
        const {searchDiagrams, productDiagrams} = diagramInstruction ? diagramInstruction : {searchDiagrams: [], productDiagrams: []};

        if (searchDiagrams.length > 0 && productDiagrams.length === 0) {
            return this.getColorForIndex(
                searchDiagrams.findIndex(
                    (searchDiagram) =>
                        (searchDiagram.searchterm
                            ? searchDiagram.searchterm === rowData[KPIGroupKey.Searchterm]
                            : rowData[KPIGroupKey.Searchterm] === '') &&
                        searchDiagram.channel === rowData[KPIGroupKey.Channel] &&
                        searchDiagram.kpi === columnDef.dataKey
                )
            );
        } else if (productDiagrams.length > 0 && searchDiagrams.length === 0) {
            return this.getColorForIndex(
                productDiagrams.findIndex(
                    (productDiagram) =>
                        (productDiagram.productId
                            ? productDiagram.productId === rowData[KPIGroupKey.ProductId]
                            : rowData[KPIGroupKey.ProductId] === '') &&
                        productDiagram.channel === rowData[KPIGroupKey.Channel] &&
                        productDiagram.kpi === columnDef.dataKey
                )
            );
        } else {
            return 'transparent';
        }
    }

    private generateCellsPerRow(
        rowData: TableDataForChannelResponse,
        lineNumber: number,
        columns: ToggleableSelectorTableColumnDef[]
    ): React.ReactNode[] {
        return columns.reduce((cells: React.ReactNode[], column) => {
            if (column.isVisible) {
                const cellBackgroundColor = this.getBackgroundColorForCell(rowData, column);
                const cellTextColor = getReadableTextColor(cellBackgroundColor);
                const fontStyle = rowData.isCompareRangeLine ? 'italic' : 'normal';
                const cellTextColorCompare = cellTextColor === BlackColor && rowData.isCompareRangeLine ? GreyColor : cellTextColor;
                cells.push(
                    <td
                        className={tableCss.tableCell}
                        style={{backgroundColor: cellBackgroundColor, color: cellTextColorCompare, fontStyle}}
                        key={column.dataKey}>
                        {column.dataKey === NoneKPIColumns.RowNumber ? lineNumber : formatValue(rowData[column.dataKey], column.valueFormat)}
                    </td>
                );
            }
            return cells;
        }, []);
    }

    private applyTargetCurrencyFormatting(): ToggleableSelectorTableColumnDef[] {
        const {mergedBuildingBlockInstructions} = this.props;
        if (mergedBuildingBlockInstructions.tableInstruction && mergedBuildingBlockInstructions.tableInstruction.activeViews.length === 1) {
            return this.props.columns.map((column) => {
                return {
                    ...column,
                    valueFormat: column.usesMoneyFormat ? this.props.moneyFormatToTargetCurrency() : column.valueFormat,
                };
            });
        }
        return [];
    }

    private renderTableBodyLines() {
        const columns = this.applyTargetCurrencyFormatting();
        return this.tablePreviewStore.data.map((rowData, index) => {
            if (!rowData) {
                return null;
            }
            return (
                <tr
                    className={(index + 1) % 2 === 1 ? tableCss.coloredBodyRow : undefined}
                    key={
                        (rowData[KPIGroupKey.Searchterm] || rowData[KPIGroupKey.ProductId]) +
                        rowData[KPIGroupKey.Channel] +
                        rowData.isCompareRangeLine
                    }>
                    {this.generateCellsPerRow(rowData, index + 1, columns)}
                </tr>
            );
        });
    }

    private renderTable() {
        return (
            <table className={tableCss.table}>
                <thead>
                    <tr className={tableCss.tableHeader}>{this.renderColumnHeaders()}</tr>
                </thead>
                <tbody>{this.renderTableBodyLines()}</tbody>
            </table>
        );
    }

    public render() {
        return (
            <div className={classes(tableCss.tableContainer, this.props.previewModeEnabled ? 'preview' : undefined)}>
                {this.tablePreviewStore.dataLoading ? this.renderLoadingSpinner() : this.renderTable()}
            </div>
        );
    }
}

const tableCss = stylesheet({
    tableHeaderCell: {
        paddingLeft: 5,
        paddingRight: 5,
    },
    tableCell: {
        padding: 4,
    },
    coloredBodyRow: {
        backgroundColor: TableRowBackgroundColor,
    },
    tableHeader: {
        color: WhiteColor,
        backgroundColor: CiBlueColor,
    },
    loadingSpinnerContainer: {
        position: 'relative',
        width: 100,
        height: 100,
        borderRadius: 2,
        overflow: 'hidden',
    },
    loadingSpinnerCentering: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    tableContainer: {
        marginTop: 5,
        paddingLeft: 5,
        paddingRight: 5,
        width: '100%',
        maxHeight: 300,
        overflow: 'auto',
        $nest: {
            '&.preview': {
                maxHeight: 'none',
            },
        },
    },
    table: {
        textAlign: 'left',
        fontFamily: '"Arial"',
        fontSize: 13,
        width: '100%',
    },
});



// WEBPACK FOOTER //
// ./src/report/views/PaperSheetBlocks/TablePreview.tsx